import React from "react";
import { Box, Hidden, Stack, Text } from "@origin-digital/ods-core";
import {
  Carousel as CarouselBase,
  CarouselSlide,
} from "@origin-digital/ods-carousel";
import type { SwiperProps } from "swiper/react";
import styled from "styled-components";
import { ICategoryDto as ICategory } from "./../../../../contract";

import {
  PopularCategories,
  PopularCategoriesSkeleton,
} from "../../../../components/PopularCategories";
import { OtherCategories } from "../../../../components/PopularCategories/OtherCategories";
import { CategoryCard } from "../../../../components/PopularCategories/CategoryCard";
import { OtherCategoryCard } from "../../../../components/PopularCategories/OtherCategoryCard";
import { content } from "./content";
import { useCategories } from "../../../../hooks";

// Carousel's children type is strictly typed to SwiperSlide elements, which
// conflicts with dynamically-mapped arrays. Cast to accept ReactNode children.
const Carousel = CarouselBase as React.ComponentType<{
  id: string;
  pagination?: string;
  swiperProps?: SwiperProps;
  children?: React.ReactNode;
}>;

// Extends the carousel to both screen edges at all sizes,
// cancelling the page gutter so the next slide peeks through.
const MobileCarouselBox = styled(Box)`
  .swiper-pagination {
    margin: 16px 16px 0;
  }
`;

const POPULAR_CATEGORIES_COUNT = 3;

const CATEGORIES_CAROUSEL_SWIPER_PROPS: SwiperProps = {
  slidesPerView: 1.2,
  spaceBetween: 16,
  slidesOffsetBefore: 16,
  slidesOffsetAfter: 16,
  keyboard: { enabled: true },
  breakpoints: {
    576: { slidesPerView: 1.6, slidesOffsetBefore: 16, slidesOffsetAfter: 16 },
  },
};

interface CategoriesMobileCarouselProps {
  popularCategories: ICategory[];
  otherCategories: ICategory[];
}

const CategoriesMobileCarousel: React.FC<CategoriesMobileCarouselProps> = ({
  popularCategories,
  otherCategories,
}) => (
  <Hidden above="lg">
    <MobileCarouselBox data-id="categories-carousel">
      <Carousel
        id="categories-carousel"
        pagination="bullets"
        swiperProps={CATEGORIES_CAROUSEL_SWIPER_PROPS}
      >
        {popularCategories.map((category) => (
          <CarouselSlide key={`popular-${category.id}`}>
            <CategoryCard category={category} />
          </CarouselSlide>
        ))}
        {otherCategories.map((category) => (
          <CarouselSlide key={`other-${category.id}`}>
            <OtherCategoryCard category={category} />
          </CarouselSlide>
        ))}
      </Carousel>
    </MobileCarouselBox>
  </Hidden>
);

export const CategoriesSection: React.FC = () => {
  const { categories, loading, error } = useCategories();
  const popularCategories = categories.slice(0, POPULAR_CATEGORIES_COUNT);
  const otherCategories = categories.slice(POPULAR_CATEGORIES_COUNT);

  return (
    <Stack space={{ xs: "medium", lg: "large" }} data-id="categories-section">
      {loading && <PopularCategoriesSkeleton />}

      {!loading && !error && categories.length === 0 && (
        <Text>{content.emptyStateMessage}</Text>
      )}

      {!loading && !error && categories.length > 0 && (
        <CategoriesMobileCarousel
          popularCategories={popularCategories}
          otherCategories={otherCategories}
        />
      )}

      {!loading && !error && popularCategories.length > 0 && (
        <Hidden below="lg">
          <PopularCategories categories={popularCategories} />
        </Hidden>
      )}

      {!loading && !error && otherCategories.length > 0 && (
        <Hidden below="lg">
          <OtherCategories categories={otherCategories} />
        </Hidden>
      )}

      {/* Error state is handled globally by ErrorBoundary via hook throws. */}
    </Stack>
  );
};
